import { environmentForAccountId, type Environment } from '../config/accounts'

/**
 * Raw shape a role is built from before being parsed. `roleArn` is synthesized from the
 * Authorization API's list-accounts response (see api/authorizationApi.ts's fetchSSORoles) --
 * there is no real SAML principal for an AWS SSO device-flow role, so `principalArn` is always
 * empty.
 */
export interface ApiRole {
  roleArn: string
  principalArn: string
}

/**
 * Simplified version of cdh-data-portal-internal's Role type. That app enriches roles with
 * AWS-account/hub/model metadata that has no equivalent in ops-hub, so this keeps only what
 * we need for support-operator role gating: the account the role belongs to and a friendly name.
 * `environment` is only set for roles from an allowed ops-hub account (see config/accounts.ts) --
 * api/authorizationApi.ts discards any role for which it would be undefined.
 */
export interface Role extends ApiRole {
  accountId: string
  environment?: Environment
  friendlyName: string
}

const ROLE_ARN_PATTERN = /^arn:([-a-z0-9]+):(?:iam|sts)::(\d{12}):(?:role|assumed-role)\/([-_a-zA-Z0-9]+)\/?([.*-_a-zA-Z0-9]+)?$/

/**
 * Extracts the AWS account ID and a friendly role name from a role ARN, e.g.
 * "arn:aws:iam::123456789012:role/SupportOperator" -> { accountId: "123456789012", friendlyName: "SupportOperator" }.
 */
export function parseRoleArn(roleArn: string): { accountId: string; friendlyName: string } {
  const match = ROLE_ARN_PATTERN.exec(roleArn)

  if (!match) {
    throw new Error(`Role ARN could not be parsed: ${roleArn}`)
  }

  return { accountId: match[2], friendlyName: match[3] }
}

export function generateRole(role: ApiRole): Role {
  const { accountId, friendlyName } = parseRoleArn(role.roleArn)

  return { ...role, accountId, environment: environmentForAccountId(accountId), friendlyName }
}
